/**
 * Firebase project settings
 */

import { merge } from 'lodash';

// Optional bundler defines. Keep their types with the shared consumer so Node
// and test projects do not need to include the client's ambient declarations.
declare const __FIREBASE_DATABASE_URL_OVERRIDE__: string | null | undefined;
declare const __FIREBASE_CLOUD_FUNCTIONS_ORIGIN_OVERRIDE__: string | null | undefined;
declare const __FIREBASE_ORIGIN_OVERRIDE__: string | null | undefined;

const authTokenUrl = 'https://www.googleapis.com/oauth2/v4/token';
const authCodeUrl = 'https://accounts.google.com/o/oauth2/v2/auth';
const mobileRedirectPath = '/auth-handler';

const settings = {
  prod: {
    googleCdn: {
      host: 'https://download.pop.com',
    },
    firebaseConfig: {
      apiKey: 'AIzaSyDGmsswv_AXiNfvogwMrBBgRjWK7oTb6VA',
      authDomain: 'pop.com',
      databaseURL: 'https://scrn-prod.firebaseio.com',
      projectId: 'scrn-prod',
      storageBucket: 'scrn-prod.appspot.com',
      messagingSenderId: '164108522845',
      appId: '1:164108522845:web:b39fe309cd9034cf',
    },
    googleClientId: {
      webClientId: '164108522845-p8h14uvegbqatkgsdg8gn5i5gbgkd0qj.apps.googleusercontent.com',
      webClientReverseId: 'com.googleusercontent.apps.164108522845-p8h14uvegbqatkgsdg8gn5i5gbgkd0qj',
      iosClientId: '164108522845-dultpa7sks6vrnpqgj91lk2cfli4pdvb.apps.googleusercontent.com',
      iosClientReverseId: 'com.googleusercontent.apps.164108522845-dultpa7sks6vrnpqgj91lk2cfli4pdvb',
    },
    firebaseCloudFunctionUrlOrigin: 'https://pop.com',
  },
  beta: {
    googleCdn: {
      host: 'https://storage.googleapis.com/pop-vo.appspot.com',
    },
    firebaseConfig: {
      apiKey: 'AIzaSyDL7QKmsjaQ1knlSRhKqikhLOf11qcooyk',
      authDomain: 'beta.pop.com',
      databaseURL: 'https://pop-vo-default-rtdb.firebaseio.com',
      projectId: 'pop-vo',
      storageBucket: 'pop-vo.firebasestorage.app',
      messagingSenderId: '117938112290',
      appId: '1:117938112290:web:da9c8818f7f2c2740f877b',
    },
    googleClientId: {
      webClientId: '117938112290-nct57p2jq4h9viu2h2kumse6dkf3k4va.apps.googleusercontent.com',
      webClientReverseId: 'com.googleusercontent.apps.117938112290-nct57p2jq4h9viu2h2kumse6dkf3k4va',
    },
    firebaseCloudFunctionUrlOrigin: 'https://beta.pop.com',
    // firebaseCloudFunctionUrlOrigin: 'http://localhost:5001/pop-vo/us-central1',
  },
  ci: {
    googleCdn: {
      host: 'https://storage.googleapis.com/iteleport-ci.appspot.com',
    },
    firebaseConfig: {
      apiKey: 'AIzaSyDyGBya9BL_-_6oJnWTSW7SKUncPrubUmo',
      authDomain: 'ci.pop.com',
      databaseURL: 'https://iteleport-ci.firebaseio.com',
      projectId: 'iteleport-ci',
      storageBucket: 'iteleport-ci.appspot.com',
      messagingSenderId: '417481078658',
    },
    googleClientId: {
      webClientId: '???',
      webClientReverseId: '???',
      iosClientId: '???',
      iosClientReverseId: '???',
    },
    firebaseCloudFunctionUrlOrigin: 'https://ci.pop.com',
  },
  dev: {
    googleCdn: {
      host: 'https://storage.googleapis.com/iteleport-nightly.appspot.com',
    },
    firebaseConfig: {
      apiKey: 'AIzaSyC7OE533SsweZlE2uLWp68NM2XWT-O7Kck',
      authDomain: 'dev.pop.com',
      databaseURL: 'https://iteleport-nightly.firebaseio.com',
      projectId: 'iteleport-nightly',
      storageBucket: 'iteleport-nightly.appspot.com',
      messagingSenderId: '505390686937',
    },
    googleClientId: {
      webClientId: '505390686937-ql159qeo1lucheaqgvf7a6m5njpustu0.apps.googleusercontent.com',
      webClientReverseId: 'com.googleusercontent.apps.505390686937-ql159qeo1lucheaqgvf7a6m5njpustu0',
      iosClientId: '505390686937-qll83oc5fa6juj0hd0pa0rr3fmlfdt7k.apps.googleusercontent.com',
      iosClientReverseId: 'com.googleusercontent.apps.505390686937-qll83oc5fa6juj0hd0pa0rr3fmlfdt7k',
    },
    firebaseCloudFunctionUrlOrigin: 'https://dev.pop.com',
  },
};

/*
 * Isolated PR previews (docs/PR_PREVIEW_PLAN_V4.md) run the regular dev functions, floof and arena
 * code against the PR's own RTDB instance in the dev project. Node processes get the instance and
 * the PR channel (their functions origin) as POP_PREVIEW_* env; the browser gets the database via
 * the `__FIREBASE_DATABASE_URL_OVERRIDE__` define. The patterns mirror ./preview-channel.ts (this
 * file must keep no relative imports: tools load it standalone); a test pins them together.
 */
export const firebaseConfigPreviewPatterns = {
  databaseUrl: /^https:\/\/iteleport-nightly-pr\d+\.firebaseio\.com$/,
  channelOrigin: /^https:\/\/iteleport-nightly--pr\d+-[a-z0-9]{8}\.web\.app$/,
};

/** `{}` unless on dev with POP_PREVIEW_* set; invalid values throw instead of falling back to dev. */
export function getPreviewRuntimeOverrides(
  settingsKey: string,
  env: Record<string, string | undefined>,
): { firebaseConfig?: { databaseURL: string }; firebaseCloudFunctionUrlOrigin?: string } {
  const databaseURL = env.POP_PREVIEW_DATABASE_URL;
  const functionsOrigin = env.POP_PREVIEW_FUNCTIONS_ORIGIN;
  if (!databaseURL && !functionsOrigin) return {};
  if (settingsKey !== 'dev')
    throw new Error(`POP_PREVIEW_* is only valid with POP_ENV=dev (got ${settingsKey})`);
  if (!databaseURL || !firebaseConfigPreviewPatterns.databaseUrl.test(databaseURL)) {
    throw new Error(`POP_PREVIEW_DATABASE_URL is not a dev preview RTDB instance: ${databaseURL}`);
  }
  if (!functionsOrigin || !firebaseConfigPreviewPatterns.channelOrigin.test(functionsOrigin)) {
    throw new Error(`POP_PREVIEW_FUNCTIONS_ORIGIN is not a dev preview channel: ${functionsOrigin}`);
  }
  return { firebaseConfig: { databaseURL }, firebaseCloudFunctionUrlOrigin: functionsOrigin };
}

export type FirebaseSettingsKey = keyof typeof settings;

const firebaseSettingsKeys = Object.keys(settings) as FirebaseSettingsKey[];

function isFirebaseSettingsKey(value: string | undefined): value is FirebaseSettingsKey {
  return value !== undefined && firebaseSettingsKeys.includes(value as FirebaseSettingsKey);
}

function getSelectedSettingsKey(): FirebaseSettingsKey {
  if (isFirebaseSettingsKey(process.env.POP_ENV)) return process.env.POP_ENV;
  if (!process.env.POP_ENV) throw new Error('POP_ENV must be defined');
  throw new Error(
    `POP_ENV must be one of: ${firebaseSettingsKeys.join(', ')}. Received ${process.env.POP_ENV}`,
  );
}

export const selectedSettingsKey = getSelectedSettingsKey();
const pristineSelectedSettings = settings[selectedSettingsKey];

// Firebase Auth injects FIREBASE_AUTH_EMULATOR_HOST as the source of truth for its emulator.
const googleIdentityToolkitLoginUrl = 'identitytoolkit.googleapis.com/v1/accounts:signInWithPassword';
const firebaseAuthEmulatorHost = globalThis.process?.env?.FIREBASE_AUTH_EMULATOR_HOST;
const googleIdentityToolkitPrefix = firebaseAuthEmulatorHost
  ? `http://${firebaseAuthEmulatorHost}/`
  : 'https://';
const identityToolkitLoginUrl = `${googleIdentityToolkitPrefix}${googleIdentityToolkitLoginUrl}?key=${pristineSelectedSettings.firebaseConfig.apiKey}`;

const hasFirebaseCloudFunctionsOriginOverride =
  typeof __FIREBASE_CLOUD_FUNCTIONS_ORIGIN_OVERRIDE__ !== 'undefined';

function getValidatedEmulatorFunctionsPort(): string | undefined {
  const value = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env
    ?.EMULATOR_FUNCTIONS_PORT;
  if (value === undefined || value === '') return;

  const port = Number.parseInt(value, 10);
  if (!/^\d{1,5}$/.test(value) || port < 1 || port > 65535) {
    console.warn(`Ignoring EMULATOR_FUNCTIONS_PORT with invalid value "${value}"`);
    return;
  }

  return String(port);
}

const emulatorFunctionsPort = hasFirebaseCloudFunctionsOriginOverride
  ? undefined
  : getValidatedEmulatorFunctionsPort();

export const selectedSettings = merge(pristineSelectedSettings, {
  googleClientId: {
    authTokenUrl,
    authCodeUrl,
    identityToolkitLoginUrl,
    mobileRedirectPath,
    mobileRedirectOrigin: pristineSelectedSettings.firebaseConfig.authDomain,
    calendarAppScriptClientId: '164108522845-p57gpjtihvosufh2vcv1sbv84gn7qt5m.apps.googleusercontent.com',
  },

  // Vite defines (CLI-controlled config, computed at build time for browser)
  ...(typeof __FIREBASE_DATABASE_URL_OVERRIDE__ !== 'undefined' && __FIREBASE_DATABASE_URL_OVERRIDE__
    ? { firebaseConfig: { databaseURL: __FIREBASE_DATABASE_URL_OVERRIDE__ } }
    : {}),
  ...(hasFirebaseCloudFunctionsOriginOverride && __FIREBASE_CLOUD_FUNCTIONS_ORIGIN_OVERRIDE__
    ? { firebaseCloudFunctionUrlOrigin: __FIREBASE_CLOUD_FUNCTIONS_ORIGIN_OVERRIDE__ }
    : {}),
  ...(typeof __FIREBASE_ORIGIN_OVERRIDE__ !== 'undefined' && __FIREBASE_ORIGIN_OVERRIDE__
    ? { origin: __FIREBASE_ORIGIN_OVERRIDE__ }
    : {
        origin:
          globalThis.location?.origin ?? `https://${pristineSelectedSettings.firebaseConfig.authDomain}`,
      }),

  // Node.js env var fallback (for floof and other server processes where Vite defines don't exist)
  ...(typeof __FIREBASE_DATABASE_URL_OVERRIDE__ === 'undefined' &&
  globalThis.process?.env?.EMULATOR_DATABASE_PORT
    ? {
        firebaseConfig: {
          databaseURL: `http://127.0.0.1:${globalThis.process.env.EMULATOR_DATABASE_PORT}?ns=iteleport-nightly`,
        },
      }
    : {}),

  // Devkit's emulated functions and server processes build cloud-function URLs locally. This
  // keeps Okta and Google OAuth redirect_uri values pointed at the functions emulator.
  ...(!hasFirebaseCloudFunctionsOriginOverride && emulatorFunctionsPort
    ? {
        // Firebase v2 functions default to us-central1; match the client and devkit URL shape.
        firebaseCloudFunctionUrlOrigin: `http://localhost:${emulatorFunctionsPort}/${pristineSelectedSettings.firebaseConfig.projectId}/us-central1`,
      }
    : {}),

  // Isolated PR preview functions / floof / arena (Node): the PR's database and channel.
  ...getPreviewRuntimeOverrides(selectedSettingsKey, globalThis.process?.env ?? {}),
});

/**
 * Google's OAuth client only accepts registered redirect URIs, and a PR channel (a new URL per PR)
 * is never one: previews sign in through the env's own `/api/handleGoogleLoginRedirect`, which
 * returns to the channel (its origin is on the login-redirect allowlist). Auth is project-wide, so
 * the resulting session works against the preview's database too.
 */
export function resolveGoogleLoginFunctionUrlOrigin(functionsOrigin: string, pristineOrigin: string): string {
  return firebaseConfigPreviewPatterns.channelOrigin.test(functionsOrigin) ? pristineOrigin : functionsOrigin;
}

export const googleLoginFunctionUrlOrigin = resolveGoogleLoginFunctionUrlOrigin(
  selectedSettings.firebaseCloudFunctionUrlOrigin,
  pristineSelectedSettings.firebaseCloudFunctionUrlOrigin,
);
// export type FirebaseProjectKey = 'prod' | 'ci' | 'dev' | 'j' | 'mh' | 'beta';
export type FirebaseProjectKey = 'prod' | 'dev' | 'beta';

export const firebaseProjectKeyToAppName: { [K in FirebaseProjectKey]: string } = {
  prod: 'Pop',
  dev: 'Pop-dev',
  beta: 'Pop-beta',
};

/*

Firebase Config Override Priority (later wins):
-----------------------------------------------

Browser (Vite builds):
1. Vite defines (__FIREBASE_*_OVERRIDE__) - Set by CLI, computed at build time from port env vars

Node.js (floof, cloud functions):
1. EMULATOR_DATABASE_PORT env var - Passed by CLI, used to construct database URL
2. EMULATOR_FUNCTIONS_PORT env var - Passed by CLI, used to construct the cloud-functions URL prefix

The CLI (`pop devkit start`) passes port env vars (EMULATOR_DATABASE_PORT,
EMULATOR_FUNCTIONS_PORT, etc.) which:
- In browser: Vite compiles into defines at build time
- In Node.js: Used directly via process.env to construct override URLs

*/
